import { type UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { QueryKey } from '../../utils/api';
import { useBffPostMutation } from '../bff';
import { BffQueryKey } from '../model/BffQueryKey';
import { useBasket } from './useBasket';
import type { PageSource } from '../../types/page-source';
import type { HttpParams } from '../../utils/url-utils';
import type { Basket, NewProductItem } from './model';

export const useAddProductItems = (
    pageSource?: PageSource,
): UseMutationResult<Basket, unknown, NewProductItem[]> => {
    const { data } = useBasket();
    const queryClient = useQueryClient();

    return useBffPostMutation<Basket, NewProductItem[]>(
        `/baskets/${data?.basketId}/product-items`,
        pageSource ? ([{ pageSource }] as HttpParams) : ([] as HttpParams),
        [BffQueryKey.BASKET_AVAILABILITY],
        {
            basketCustomerId: data?.customerInfo?.customerId,
            onSuccess: async (basket) => {
                queryClient.setQueryData([BffQueryKey.BASKET], basket);

                await Promise.all([
                    queryClient.invalidateQueries([
                        QueryKey.APPLE_PAY_REQUEST,
                        JSON.stringify({
                            isFromPWA: 'true',
                            source: 'cart',
                        }),
                    ]),
                    queryClient.invalidateQueries([BffQueryKey.PAYMENT_METHODS, ''], {
                        exact: false,
                    }),
                    queryClient.invalidateQueries(
                        [BffQueryKey.BASKET_INVENTORY, String(data?.basketId)],
                        {
                            exact: false,
                        },
                    ),
                ]);
            },
        },
    );
};
